import * as Sentry from '@sentry/react';
import { KitUtilCommon } from '@chargepoint/cp-toolkit';
import i18n from 'i18next';

// Util functions

function getCommonRequestHeaders(i18n) {
  const acceptsLanguage = i18n.language ? i18n.language : '*';

  // Add Sentry transaction id header to allow for distributed tracing
  // https://sentry-docs-git-patch-1.sentry.dev/product/sentry-basics/guides/error-tracing/
  const transactionId = Math.random().toString(36).substring(2, 11);
  Sentry.configureScope((scope) => {
    scope.setTag('transaction_id', transactionId);
  });

  const requestHeaders = {
    'Content-Type': 'application/json',
    'Accept-Language': acceptsLanguage,
    'X-Transaction-ID': transactionId,
  };

  return requestHeaders;
}

function getDomain() {
  const urlParts = window.location.hostname.split('.');
  return urlParts
    .slice(0)
    .slice(-(urlParts.length === 4 ? 3 : 2))
    .join('.');
}

function getSubdomain() {
  let subdomain =
    window.location.hostname === 'localhost' ? 'sso' : window.location.host.split('.')[0];
  if (subdomain === 'sso-local') {
    subdomain = 'sso';
  }
  return subdomain;
}

function makeSsoApiUrl(endpoint) {
  return `/api${endpoint}`;
}

function getNosSubDomain(localePickerResult) {
  if (!localePickerResult) {
    return window.cp_constants.NOS_SUBDOMAIN;
  }

  switch (window.cp_constants.ENV) {
    case 'qa':
      return localePickerResult.nosSubdomain.qa || localePickerResult.nosSubdomain.default;
    case 'localdev':
      return localePickerResult.nosSubdomain.localdev || localePickerResult.nosSubdomain.default;
    default:
      return localePickerResult.nosSubdomain.default;
  }
}

// NOTES:
// The signup url does NOT accept a redirect query param... BUT we still need to pass along to sign up
// any of the query params that may have been on the redirect URL.
// Example:
// https://sso-local.ev-chargepoint.com/?redirect=https%3A%2F%2Fna.chargepoint.com%2Fconnections%2FZTJmZDc2NTVdDKuw40QpZuDZwx_rnPOXblAiN7Xp9mFl4y3hnvq0FLANA8AE4AbeobZOFaObU9U%253D%3FpartnerToken%3DWP170379SDG%26connection%3D30521%26requiredInstance%3Dus
// https://signup.ev-chargepoint.com/signup/profile?partnerToken=WP170379SDG&connection=30521&requiredInstance=us
// Please reach out to Brandon or Peter if you need help understanding this.

function makeSignUpUrl(localePickerResult) {
  let signupUrl = `https://signup.${getDomain()}/signup/profile`;

  const queryParamsObj = KitUtilCommon.getQueryObject(window.location.search);
  if (localePickerResult && queryParamsObj) {
    // Pull out any query params that might be in the redirect URL
    const { hasOwnProperty } = Object.prototype;
    if (hasOwnProperty.call(queryParamsObj, 'redirect')) {
      const redirectUrlString = KitUtilCommon.getQueryParam(window.location.search, 'redirect');
      try {
        const redirectUrl = new URL(redirectUrlString);
        const redirectUrlQueryParamsObj = KitUtilCommon.getQueryObject(redirectUrl.search);
        Object.assign(queryParamsObj, redirectUrlQueryParamsObj);
      } catch (e) {
        if (e instanceof TypeError) {
          console.error('Invalid redirect URL', redirectUrlString);
        } else {
          throw e;
        }
      }
    }

    // Always pass the current locale
    queryParamsObj.locale = localePickerResult.localeCode;

    // Append all the query params to signup url string
    const updatedParams = KitUtilCommon.getQueryString(queryParamsObj);
    signupUrl += updatedParams;
  }

  return signupUrl;
}

function makePrivacyPolicyUrl(localePickerResult) {
  return `https://${getNosSubDomain(localePickerResult)}.${
    window.cp_constants.ENV_DOMAIN
  }/privacy_policy?instance=${localePickerResult.regionCode}&locale=${
    localePickerResult.localeCode
  }`;
}

function makeCaliforniaPrivacyUrl(localePickerResult) {
  return `https://${getNosSubDomain(localePickerResult)}.${window.cp_constants.ENV_DOMAIN}${
    localePickerResult.californiaPrivacyPath
  }`;
}

function validateEmail(email) {
  const re = /^.+@.+$/;
  return re.test(email);
}

const installerClientIds = ['learnupon', 'prolydian'];
const INSTALLER_URL = 'https://installer-app.chargepoint.com/signup/intro' // temporary fix for signup link

function getSignupLinkWithText(queryParams, localePickerResult) {
  const params = new URLSearchParams(queryParams)
  const redirectValue = params.get('redirect')
  if (redirectValue) {
    const { search } = new URL(redirectValue)
    const nestedParams = new URLSearchParams(search)
    if (installerClientIds.includes(nestedParams.get('client_id'))) {
      const url = window.cp_constants.ENV === 'prod' ? INSTALLER_URL : window.cp_constants.INSTALLER_URL;
      return [i18n.t('dont_have_a_field_technician_account'), `${url}?redirect=${redirectValue}`]
    }
  }
  return [i18n.t('dont_have_a_driver_account'), makeSignUpUrl(localePickerResult)]
}

// Runtime switch for the invite-link redirect below. The deployed /cp_constants.js comes from the environment's
// deploy config (not web/configs/runtime_config.json), so an absent value must still behave sensibly:
// absent -> on everywhere except prod; true / false in cp_constants always wins. Prod is switched on by adding
// "DRIVER_PORTAL_CONNECTION_LOGIN": true to its cp_constants once IMS and the Driver Portal are released.
function driverPortalConnectionLoginEnabled() {
  const constants = window.cp_constants;
  if (!constants) {
    return false;
  }
  if (typeof constants.DRIVER_PORTAL_CONNECTION_LOGIN === 'boolean') {
    return constants.DRIVER_PORTAL_CONNECTION_LOGIN;
  }
  return constants.ENV !== 'prod';
}

// EU Sunset (CORE-3425): driver sign-in and sign-up moved to the Driver Portal (driver.<domain>/login,
// backed by IMS). An unauthenticated driver who opens an organisation "Invite drivers" link arrives here as
// https://sso.<domain>/?redirect=https://<nos>.<domain>/connections/<ident>?partnerToken=<code>&connection=<id>...
// and must continue on the Driver Portal, which reads the same redirect, asks IMS to evaluate the
// connection, and lands the driver on its Request connection drawer afterwards.
// Returns that Driver Portal URL, or null when the redirect is not a connection invite on this domain
// (every other SSO flow is unchanged) or the redirect is switched off (driverPortalConnectionLoginEnabled).
function makeDriverPortalConnectionLoginUrl(search = window.location.search) {
  if (!driverPortalConnectionLoginEnabled()) {
    return null;
  }
  const redirectValue = new URLSearchParams(search).get('redirect');
  if (!redirectValue) {
    return null;
  }
  let redirectUrl;
  try {
    redirectUrl = new URL(redirectValue);
  } catch (e) {
    return null;
  }
  const domain = getDomain();
  const host = redirectUrl.hostname;
  const sameDomain = host === domain || host.endsWith(`.${domain}`);
  const secure = redirectUrl.protocol === 'https:' || window.cp_constants.ENV !== 'prod';
  const segments = redirectUrl.pathname.split('/').filter(Boolean);
  const at = segments.indexOf('connections');
  if (!sameDomain || !secure || at < 0) {
    return null;
  }
  const connection = redirectUrl.searchParams.get('connection');
  const partnerToken = redirectUrl.searchParams.get('partnerToken');
  if (!connection && !partnerToken && !segments[at + 1]) {
    return null;
  }
  const params = new URLSearchParams();
  params.set('redirect', redirectValue);
  if (connection) {
    params.set('connection', connection);
  }
  if (partnerToken) {
    params.set('partnerToken', partnerToken);
  }
  return `https://driver.${domain}/login?${params.toString()}`;
}

export default {
  getCommonRequestHeaders,
  getDomain,
  getSubdomain,
  makeSsoApiUrl,
  makeSignUpUrl,
  makePrivacyPolicyUrl,
  makeCaliforniaPrivacyUrl,
  validateEmail,
  getSignupLinkWithText,
  makeDriverPortalConnectionLoginUrl
};
